/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Clock, FileText, Stethoscope, Image, MessageSquare, 
  Plus, ChevronDown, ChevronUp, RefreshCw, Check, Eye, Edit,
  Send, Bell, X, UserPlus
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = window.__WR_API_BASE__ || '';

const TeamCollaborationPanel = ({ 
  isOpen, 
  onClose,
  onLoadNote,
  currentPatientLabel,
  user,
  setTeamUnreadCount
}) => {
  const [activeTab, setActiveTab] = useState('messages'); // 'drafts', 'messages'
  const [teamDrafts, setTeamDrafts] = useState([]);
  const [draftsByLabel, setDraftsByLabel] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedLabels, setExpandedLabels] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [selectedNote, setSelectedNote] = useState(null);
  const [addToNoteModal, setAddToNoteModal] = useState(null);
  const [addContent, setAddContent] = useState({ type: 'note', text: '', specialty: '', imaging_type: '' });

  // Fetch team drafts
  const fetchTeamDrafts = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/team/drafts/today`, {
        withCredentials: true
      });
      
      setTeamDrafts(response.data.drafts || []);
      setDraftsByLabel(response.data.by_label || {});
      setTeamMembers(response.data.team_members || []);
    } catch (error) {
      console.error('Error fetching team drafts:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load team drafts');
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch team messages
  const fetchMessages = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/team/messages`, {
        withCredentials: true
      });
      
      setMessages(response.data.messages || []);
      setUnreadCount(response.data.unread_count || 0);
      if (setTeamUnreadCount) setTeamUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [user, setTeamUnreadCount]);

  // Load data when panel opens
  useEffect(() => {
    if (isOpen && user) {
      fetchTeamDrafts();
      fetchMessages();
    }
  }, [isOpen, user, fetchTeamDrafts, fetchMessages]);

  // Auto-refresh every 30 seconds when open (was 30s, keep it)
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      fetchMessages(); // silent refresh, no state churn on drafts
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isOpen, fetchTeamDrafts, fetchMessages]);

  // Toggle label expansion
  const toggleLabel = (label) => {
    setExpandedLabels(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  // Send a message
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      await axios.post(`${API_URL}/api/team/messages`, {
        message: newMessage,
        patient_label: currentPatientLabel || null
      }, { withCredentials: true });
      
      setNewMessage('');
      fetchMessages();
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  // Mark messages as read
  const handleMarkRead = async () => {
    try {
      await axios.post(`${API_URL}/api/team/messages/mark-read`, {}, {
        withCredentials: true
      });
      fetchMessages();
    } catch (error) {
      console.error('Error marking messages read:', error);
    }
  };

  // Add content to a note
  const handleAddToNote = async () => {
    if (!addToNoteModal) return;
    
    let content = {};
    if (addContent.type === 'note') {
      content = { text: addContent.text };
    } else if (addContent.type === 'referral') {
      content = { specialty: addContent.specialty, reason: addContent.text };
    } else if (addContent.type === 'imaging') {
      content = { imaging_type: addContent.imaging_type, body_part: addContent.text };
    }
    
    try {
      await axios.post(`${API_URL}/api/team/drafts/add-to-note`, {
        note_id: addToNoteModal.id,
        add_type: addContent.type,
        content: content
      }, { withCredentials: true });
      
      toast.success(`Added ${addContent.type} to note`);
      setAddToNoteModal(null);
      setAddContent({ type: 'note', text: '', specialty: '', imaging_type: '' });
      fetchTeamDrafts();
      fetchMessages();
    } catch (error) {
      toast.error('Failed to add to note');
    }
  };

  // Format time ago
  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <CardHeader className="border-b border-border pb-3 shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-primary" />
              Team Collaboration
            </CardTitle>
            <button onClick={onClose} className="p-1 hover:bg-secondary rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Tabs — Messages first */}
          <div className="flex gap-2 mt-3">
            <Button
              variant={activeTab === 'messages' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setActiveTab('messages'); handleMarkRead(); }}
              className="flex-1 relative"
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              Messages
              {unreadCount > 0 && (
                <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-[10px]">
                  {unreadCount}
                </Badge>
              )}
            </Button>
            <Button
              variant={activeTab === 'drafts' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('drafts')}
              className="flex-1"
            >
              <FileText className="w-4 h-4 mr-1" />
              Team Drafts
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-y-auto p-4">
          {/* Team Drafts Tab */}
          {activeTab === 'drafts' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{teamMembers.length} team members • {teamDrafts.length} drafts today</span>
                <Button variant="ghost" size="sm" onClick={fetchTeamDrafts} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              
              {Object.keys(draftsByLabel).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No team drafts for today</p>
                  <p className="text-sm mt-1">Drafts with patient labels will appear here</p>
                </div>
              ) : (
                Object.entries(draftsByLabel).map(([label, notes]) => (
                  <div key={label} className="border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleLabel(label)}
                      className="w-full flex items-center justify-between p-3 bg-secondary/30 hover:bg-secondary/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-primary" />
                        <span className="font-medium">{label}</span>
                        <Badge variant="secondary" className="text-xs">{notes.length}</Badge>
                      </div>
                      {expandedLabels[label] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    
                    {expandedLabels[label] && (
                      <div className="divide-y divide-border">
                        {notes.map(note => (
                          <div key={note.id} className="p-3 hover:bg-secondary/20">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="font-medium truncate">
                                    {note.chief_complaint || note.note_title || 'Untitled'}
                                  </span>
                                  <span className="text-muted-foreground text-xs">
                                    by {note.author_name}
                                  </span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {timeAgo(note.created_at)}
                                  {note.imaging_orders?.length > 0 && (
                                    <span className="ml-2">• {note.imaging_orders.length} imaging</span>
                                  )}
                                  {note.referrals?.length > 0 && (
                                    <span className="ml-2">• {note.referrals.length} referrals</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => onLoadNote && onLoadNote(note)}
                                  title="Load into editor"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setAddToNoteModal(note)}
                                  title="Add to this note"
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
          
          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="flex flex-col h-full" style={{ minHeight: '300px' }}>
              {/* Messages List - scrollable */}
              <div className="flex-1 overflow-y-auto space-y-2 mb-3 custom-scrollbar" style={{ maxHeight: '45vh' }}>
                {messages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No messages yet</p>
                    <p className="text-sm mt-1">Team activity will appear here</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div 
                      key={msg.id} 
                      className={`p-3 rounded-lg border ${
                        msg.is_read ? 'bg-card border-border' : 'bg-primary/5 border-primary/30'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                          msg.message_type === 'activity' ? 'bg-amber-500/20 text-amber-500' : 'bg-primary/20 text-primary'
                        }`}>
                          {msg.message_type === 'activity' ? <Bell className="w-4 h-4" /> : msg.from_user_name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{msg.from_user_name}</span>
                            <span className="text-xs text-muted-foreground">{timeAgo(msg.created_at)}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5">{msg.message}</p>
                          {msg.patient_label && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              {msg.patient_label}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {/* Message Input - pinned at bottom */}
              <div className="flex gap-2 pt-2 border-t border-border shrink-0">
                <Input
                  placeholder="Send a message to your team..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  className="flex-1"
                  data-testid="team-message-input"
                />
                <Button onClick={handleSendMessage} disabled={!newMessage.trim()}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Add to Note Modal */}
      {addToNoteModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add to: {addToNoteModal.patient_label || 'Note'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">What to add</Label>
                <Select value={addContent.type} onValueChange={(v) => setAddContent(prev => ({ ...prev, type: v }))}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Additional Notes</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="imaging">Imaging Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {addContent.type === 'note' && (
                <div>
                  <Label className="text-xs text-muted-foreground">Notes to add</Label>
                  <Textarea
                    placeholder="Add your notes..."
                    value={addContent.text}
                    onChange={(e) => setAddContent(prev => ({ ...prev, text: e.target.value }))}
                    rows={4}
                  />
                </div>
              )}
              
              {addContent.type === 'referral' && (
                <>
                  <div>
                    <Label className="text-xs text-muted-foreground">Specialty</Label>
                    <Input
                      placeholder="e.g., Physical Therapy, Orthopedics"
                      value={addContent.specialty}
                      onChange={(e) => setAddContent(prev => ({ ...prev, specialty: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Reason</Label>
                    <Textarea
                      placeholder="Reason for referral..."
                      value={addContent.text}
                      onChange={(e) => setAddContent(prev => ({ ...prev, text: e.target.value }))}
                      rows={2}
                    />
                  </div>
                </>
              )}
              
              {addContent.type === 'imaging' && (
                <>
                  <div>
                    <Label className="text-xs text-muted-foreground">Imaging Type</Label>
                    <Input
                      placeholder="e.g., X-ray, MRI, CT"
                      value={addContent.imaging_type}
                      onChange={(e) => setAddContent(prev => ({ ...prev, imaging_type: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Body Part / Details</Label>
                    <Input
                      placeholder="e.g., Lumbar spine, Right knee"
                      value={addContent.text}
                      onChange={(e) => setAddContent(prev => ({ ...prev, text: e.target.value }))}
                    />
                  </div>
                </>
              )}
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAddToNoteModal(null)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleAddToNote} className="flex-1">
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TeamCollaborationPanel;
